import React, { useEffect, useState } from 'react'
import { Helmet } from "react-helmet";
import brandDomains from 'src/config/hostConfig';
import { getHrefLang } from 'src/config/seo_api';
import styled from 'styled-components';
import { theme } from 'styled-tools';

type ErrorBoundaryState = {
    hasError: boolean
}
type ErrorBoundaryProps = {
    children: React.ReactNode
}

export type MetaProps = {
    'pathname': string | null;
    'meta-title': string | null;
    'meta-description': string | null;
    'canonical': string | null;
    'alternates_(alternate_-_media_only)': string | null;
    'hreflang_(en)'?: string | null;
    'hreflang_(hi)'?: string | null;
    'hreflang_(te)'?: string | null;
    'hreflang_(en-pk)'?: string | null;
    'og_url'?: string | null;
    'og_title'?: string | null;
    'og_description'?: string | null;
    'og_type'?: string | null;
    'og_image'?: string | null;
    'og_site_name'?: string | null;
    'og_locale'?: string | null;
    'twitter_card'?: string | null;
    'twitter_title'?: string | null;
    'twitter_description'?: string | null;
    'twitter_image'?: string | null;
    'twitter_url'?: string | null;
    'twitter_site'?: string | null;
    'twitter_creator'?: string | null;
    'breadcrumb_schema'?: string | null;
    'webpage_schema'?: string | null;
    'article_schema'?: string | null;
    'faq_schema': string | null;
    'game_schema'?: string | null;
    'app_schema'?: string | null;
    'review_schema'?: string | null;
    'organization_schema'?: string | null;
    'navigation_schema'?: string | null;
    'how_to_schema'?: string | null;
    'seo_footer_title_(h1)'?: string | null;
    'seo_footer_content'?: string | null;
    'no_indexindex'?: string | null;
    'no_followfollow'?: string | null;
    'canonical_mobe'?: boolean | any;

}

type FooterSEOProp = {
    'footer_title': string | null;
    'footer_content': any
}

type DomainsProps = {
    domain: string;
    id: number;
    type: string;
    dev: string;
    main?: boolean
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: any) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error: any) {
        return { hasError: true }
    }

    render() {
        const { hasError } = this.state;
        if (this.state.hasError) {
            return <></>;
        }
        return this.props.children;
    }
}

function findandreplaceDomain(rowSchema: any, domain: string) {
    // 1. Create a shallow copy of the current object
    let schema = { ...rowSchema };

    // 2. Iterate over the object's OWN keys using Object.keys/forEach (safer than 'for...in')
    Object.keys(schema).forEach(key => {
        let val = schema[key];

        if (typeof val === 'string') {
            // Case 1: String
            schema[key] = val.replaceAll('[domain]', domain);
        }
        else if (Array.isArray(val)) {
            // Case 2: Array
            let newArray = val.map(element => {
                if (typeof element === 'string') {
                    return element.replaceAll('[domain]', domain);
                }
                // Recursively call for nested objects/arrays inside the current array
                if (typeof element === 'object' && element !== null) {
                    return findandreplaceDomain(element, domain);
                }
                // Return any other type (number, boolean, etc.) as is
                return element;
            });

            schema[key] = newArray;
        }
        else if (typeof val === 'object' && val !== null) {
            // Case 3: Nested Object (and not null)
            schema[key] = findandreplaceDomain(val, domain);
        }
        // Other types (number, boolean, function, etc.) are ignored.
    });

    return schema;
}

const SEOMeta = ({ metaData }: { metaData: MetaProps }) => {
    const [hrefData, sethrefData] = useState([] as any);
     //Changes needed for handling relative path
    const _loc = window.location;
    const _host = _loc.host;
    let _origin = _loc.origin;

    const domainProps: DomainsProps = brandDomains.find(host => host.domain.includes(_host));
    const { id, main = false, dev } = domainProps;
  
    useEffect(() => {
        const fetchHrefLang = async () => {
            try {
                const data = await getHrefLang(_loc.pathname, id);
                sethrefData(data);
            } catch (e) {
                console.log('Error in hreflang Data');
                sethrefData([]);
            }
        };

        fetchHrefLang();
    }, [id, _loc.pathname]);

    const title = metaData['meta-title'] || '';
    const description = metaData['meta-description'] || '';
    let canonical = metaData['canonical'] || '';
    let alternate = metaData['alternates_(alternate_-_media_only)'] || ''
   
    let hrefLangEn = metaData['hreflang_(en)'] || '';
    let hrefLangHi = metaData['hreflang_(hi)'] || '';
    let hrefLangTe = metaData['hreflang_(te)'] || '';

    let hrefLangPK = metaData['hreflang_(en-pk)'] || '';

    let indexindex = metaData['no_indexindex'] || '';
    let followfollow = metaData['no_followfollow'] || '';
    let canonical_mobe = metaData['canonical_mobe'] || false;

    //OG
    let og_url = metaData['og_url'] || canonical;
    const og_title = metaData['og_title'] || title;
    const og_description = metaData['og_description'] || description;
    const og_type = metaData['og_type'] || 'website';
    const og_image = metaData['og_image'] || '';
    const og_site_name = metaData['og_site_name'] || '';
    const og_locale = metaData['og_locale'] || '';

    //twitter
    const twitter_card = metaData['twitter_card'] || '';
    const twitter_title = metaData['twitter_title'] || title;
    const twitter_description = metaData['twitter_description'] || description;
    const twitter_image = metaData['twitter_image'] || '';
    let twitter_url = metaData['twitter_url'] || canonical;
    const twitter_site = metaData['twitter_site'] || '';
    const twitter_creator = metaData['twitter_creator'] || '';

    //Changes needed for handling relative path

    if (!main || dev !== 'PROD') {
        let _domain = brandDomains.find(host => host.id === id && host.main === true);
        _origin = _domain && _domain.domain ? `https://${_domain.domain}` : _origin;
    }

    const isMain = main || false;

    if (canonical) {
        let _newURL = new URL(canonical, _origin);
        canonical = canonical_mobe ? _newURL.href.replace('https://www.', 'https://m.') : _newURL.href;
    }

    if (alternate) {
        let _newURL = new URL(alternate, _origin);
        alternate = _newURL.href.replace("www", "m");
    }

    // if (hrefLangEn) {
    //     let _newURL = new URL(hrefLangEn, _origin);
    //     hrefLangEn = _newURL.href;
    // }
    if (hrefLangHi) {
        let _newURL = new URL(hrefLangHi, _origin);
        hrefLangHi = _newURL.href.replace("www", "m");
    }
    if (hrefLangTe) {
        let _newURL = new URL(hrefLangTe, _origin);
        hrefLangTe = _newURL.href.replace("www", "m");
    }

    if (og_url) {
        let _newURL = new URL(og_url, _origin);
        og_url = _newURL.href;
    }
    if (twitter_url) {
        let _newURL = new URL(twitter_url, _origin);
        twitter_url = _newURL.href;
    }

    const structuredData = () => {
        try {
            //breadcrumb_schema 
            const breadcrumb_schema = (() => {
                try {
                    const schema = JSON.parse(metaData['breadcrumb_schema']) || {};
                    const newSchema = findandreplaceDomain(schema, _origin);
                    // JSON.parse("{\"@context\":\"https://schema.org/\",\"@type\":\"BreadcrumbList\",\"itemListElement\":[{\"@type\":\"ListItem\",\"position\":1,\"name\":\"Fun88 ID\",\"item\":\"https://www.fun88.site\"}]}")
                    return newSchema && Object.keys(newSchema).length > 0 ? [newSchema] : [];
                } catch (error) {
                    return []
                }
            })();

            //faq_schema 
            const faq_schema = (() => {
                try {
                    const schema = JSON.parse(metaData['faq_schema']) || {};
                    const newSchema = findandreplaceDomain(schema, _origin);
                    return newSchema && Object.keys(newSchema).length > 0 ? [newSchema] : [];
                } catch (error) {
                    return []
                }
            })();

            //webpage_schema 
            const webpage_schema = (() => {
                try {
                    const schema = JSON.parse(metaData['webpage_schema']) || {};
                    const newSchema = findandreplaceDomain(schema, _origin);
                    return newSchema && Object.keys(newSchema).length > 0 ? [newSchema] : [];
                } catch (error) {
                    return []
                }
            })();

            //article_schema 
            const article_schema = (() => {
                try {
                    const schema = JSON.parse(metaData['article_schema']) || {};
                    const newSchema = findandreplaceDomain(schema, _origin);
                    return newSchema && Object.keys(newSchema).length > 0 ? [newSchema] : [];
                } catch (error) {
                    return []
                }
            })();

            //game_schema 
            const game_schema = (() => {
                try {
                    const schema = JSON.parse(metaData['game_schema']) || {};
                    const newSchema = findandreplaceDomain(schema, _origin);
                    return newSchema && Object.keys(newSchema).length > 0 ? [newSchema] : [];
                } catch (error) {
                    return []
                }
            })();

            //navigation_schema 
            const navigation_schema = (() => {
                try {
                    const schema = JSON.parse(metaData['navigation_schema']) || {};
                    const newSchema = findandreplaceDomain(schema, _origin);
                    return newSchema && Object.keys(newSchema).length > 0 ? [newSchema] : [];
                } catch (error) {
                    return []
                }
            })();

            //how_to_schema
            const how_to_schema = (() => {
                try {
                    const schema = JSON.parse(metaData['how_to_schema']) || {};
                    const newSchema = findandreplaceDomain(schema, _origin);
                    return newSchema && Object.keys(newSchema).length > 0 ? [newSchema] : [];
                } catch (error) {
                    return []
                }
            })();

            //app_schema
            const app_schema = (() => {
                try {
                    const schema = JSON.parse(metaData['app_schema']) || {};
                    const newSchema = findandreplaceDomain(schema, _origin);
                    return newSchema && Object.keys(newSchema).length > 0 ? [newSchema] : [];
                } catch (error) {
                    return []
                }
            })();

            //organization_schema
            const organization_schema = (() => {
                try {
                    const schema = JSON.parse(metaData['organization_schema']) || {};
                    const newSchema = findandreplaceDomain(schema, _origin);
                    return newSchema && Object.keys(newSchema).length > 0 ? [newSchema] : [];
                } catch (error) {
                    return []
                }
            })();

            //Combined All 
            const combined_schema = [
                ...breadcrumb_schema,
                ...faq_schema,
                ...webpage_schema,
                ...article_schema,
                ...game_schema,
                ...navigation_schema,
                ...how_to_schema,
                ...app_schema,
                ...organization_schema
            ];
            return JSON.stringify(combined_schema)

        } catch (error) {
            return JSON.stringify('[]')
        }
    }

    const isHrefData = hrefData.length>0;

    return (
        <Helmet>
            {title && <title>{title}</title>}

            {!isMain && <meta name="robots" content="noindex" />}
            {isMain && indexindex && <meta name="robots" content="noindex" />}
            {followfollow && <meta name="robots" content={followfollow} />}

            {description && <meta name="description" content={description} />}
            {canonical && <link rel="canonical" href={canonical} />}
            {alternate && <link rel="alternate" media="only screen and (max-width:640px)" href={alternate} />}

            {!isHrefData && hrefLangEn && <link rel="alternate" href={canonical} hrefLang="x-default" />}
            {!isHrefData && hrefLangEn && <link rel="alternate" href={canonical} hrefLang="en-IN" />}
            {!isHrefData && hrefLangHi && <link rel="alternate" href={hrefLangHi} hrefLang="hi-IN" />}
            {!isHrefData && hrefLangTe && <link rel="alternate" href={hrefLangTe} hrefLang="te-IN" />}
            {!isHrefData && hrefLangPK && <link rel="alternate" href={hrefLangPK} hrefLang="en-PK" />}
            
            {
                hrefData.map(({ rel, hreflang, href }) => (
                    <link
                        key={`${hreflang}-${href}`}
                        rel={rel}
                        hrefLang={hreflang}
                        href={href}
                    />
                ))
            }

            {og_title && <meta property="og:title" content={og_title} />}
            {og_site_name && <meta property="og:site_name" content={og_site_name} />}
            {og_url && <meta property="og:url" content={og_url} />}
            {og_description && <meta property="og:description" content={og_description} />}
            {og_type && <meta property="og:type" content={og_type} />}
            {og_image && <meta property="og:image" content={og_image} />}
            {og_locale && <meta property="og:locale" content={og_locale} />}

            {twitter_card && <meta name="twitter:card" content={twitter_card} />}
            {twitter_title && <meta name="twitter:title" content={twitter_title} />}
            {twitter_description && <meta name="twitter:description" content={twitter_description} />}
            {twitter_image && <meta name="twitter:image" content={twitter_image} />}
            {twitter_url && <meta name="twitter:url" content={twitter_url} />}
            {twitter_site && <meta name="twitter:site" content={twitter_site} />}
            {twitter_creator && <meta name="twitter:creator" content={twitter_site} />}

            <script type="application/ld+json">
                {
                    structuredData()
                }
            </script>

        </Helmet>
    )

}

const SEOFooter = (props: FooterSEOProp) => {
    let { footer_title, footer_content } = props;

    if (!footer_content) {
        return <></>
    }

    return (
        <AccordionWrapper>
            <Accordion>
                <input type="checkbox" id="section1" className="accordion__input" />
                <label htmlFor="section1" className="accordion__label"><h1>{footer_title}</h1></label>
                <div className='accordion__container' dangerouslySetInnerHTML={{ __html: footer_content }}>
                </div>
            </Accordion>
        </AccordionWrapper>
    )
}
//export const SEOMetaCommon = ({metaData}:{metaData:MetaProps}) => {
export const SEOMetaCommon = ({ metaData }: { metaData: any }) => {
    return (
        <ErrorBoundary>
            <SEOMeta metaData={metaData} />
        </ErrorBoundary>
    )
}

export const SEOFooterCommon = (props: FooterSEOProp) => {
    const { footer_title = '', footer_content = '' } = props;

    return (
        <ErrorBoundary>
            <SEOFooter footer_title={footer_title} footer_content={footer_content} />
        </ErrorBoundary>
    )
}

//Styled Components
const Accordion = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    color: ${theme('palette.seoContenAccordionHeadTextColor')};
    background: ${theme('palette.seoContenAccordionHeadBg')};

    .accordion__label, .accordion__container {
        padding: 14px 20px;
        
    }

    h1 {
        display: block;
        font-size: 1em;
        font-weight: bold;
        margin: 0;
    }

    h2 {
        display: block;
        font-size: 1.5em;
        font-weight: bold;
    }

    h3 {
        display: block;
        font-size: 1.1em;
        font-weight: bold;
    }

    .number {
        list-style: number;

        li {
            font-weight: bold;
        }
    }

    ul {
        li {
            font-size: 0.91em;
        }
    }

    p {
        margin: 5px 0 10px 0;
        color: ${theme('palette.seoContenAccordionInnerTextColor')};
        font-size: 0.91em;
        font-weight: normal;
        text-transform: none;
    }

    ol, ul {
        li {
            text-transform: none;
            font-size: 0.91em;
        }
    }

    a {
        color: ${theme('palette.seoContenAccordionInnerAngerTagColor')};
    }

    .linkBg {
        display: flex;
        align-items: center;
        justify-content: left;
        flex-wrap: wrap;
        gap: 5px;

        a {
            background: #1e1e1e;
            border-radius: 5px;
            padding: 5px 10px;
        }
    }

    .textTransformNone {
        text-transform: none;
    }

    .accordion__label{
        display: flex;
        align-items: center;
        justify-content: center;
        color: ${theme('palette.seoContenAccordionHeadTextColor')};
        padding: 20px;
        background: ${theme('palette.seoContenAccordionHeadBg')};
        text-align: center;
        position: relative;
        cursor: pointer;

        :after {
            content: "";
            display: inline-block;
            border-style: solid;
            border-color: ${theme('palette.seoContenAccordionArrowBorder')};
            border-width: 0 2px 2px 0;
            padding: 2.5px;
            vertical-align: middle;
            transition: transform 0.3s;
            transform: ${theme('palette.seoContenAccordionArrowMarkAfter')};
            position: absolute;
            right: 20px;
        }
    }

    .accordion__label:hover{
        background: ${theme('palette.seoContenAccordionHeadHoverBg')};
    }

    .accordion__container {
        background: ${theme('palette.seoContenAccordionContentBg')};
        line-height: 1.6;
        font-size: 0.85em;
        opacity: ${theme('palette.seoContenAccordionContentOpacityOpen')};
        text-transform: capitalize;
        max-height: ${theme('palette.seoContenAccordionContentMaxHeightOpen')};
        padding: ${theme('palette.seoContenAccordionContentPaddingOpen')};
        margin: ${theme('palette.seoContenAccordionContentMarginOpen')};
        overflow-x: ${theme('palette.seoContenAccordionContentOverFlowXOpen')};
        overflow-y: ${theme('palette.seoContenAccordionContentOverFlowYOpen')};
        transition: 0.3s;
        word-wrap: break-word;

        &::-webkit-scrollbar-track {
            -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
            background-color: #f5f5f5;
            border-radius: 10px;
        }

        &::-webkit-scrollbar {
          width: 10px;
          background-color: #f5f5f5;
        }
    
        &::-webkit-scrollbar-thumb {
          border-radius: 10px;
          background:gray;
          margin-bottom: 10px;
        }
    }

    .accordion__input {
        display: none;
    }

    .accordion__input:checked ~ .accordion__container {
        opacity: ${theme('palette.seoContenAccordionContentOpacityClosed')};
        max-height: ${theme('palette.seoContenAccordionContentMaxHeightClosed')};
        padding: ${theme('palette.seoContenAccordionContentPaddingClosed')};
        margin: ${theme('palette.seoContenAccordionContentMarginClosed')};
    }

    .accordion__input:checked ~ .accordion__label:after {
        transform: ${theme('palette.seoContenAccordionArrowMarkBefore')};
    }

    .td,th{
        border: 1px solid white;
    }
  
    .table, th, td {
  border: 1px solid white;
  border-collapse: collapse;
}

`;

const AccordionWrapper = styled.div`
    width: 100%;
    table, th, td {
        border: 1px solid white;
    }
`;